import { getBasename } from "../../routes/basename";
import { DOCUMENT_VIEWER_PATH } from "../../routes/paths";
import { OPENER_LOCK_KEY } from "./coord/openerLock";

/** Named target so repeat clicks reuse a single viewer window across tabs. */
const VIEWER_WINDOW_NAME = "dsc-doc-viewer";

// Module-scoped so repeat clicks within the same tab reuse the reference;
// the named target dedupes across tabs (mirrors the dsc-dual-window-poc).
let viewerWindow: Window | undefined;

/**
 * Builds the `window.open` features string that forces a separate browser
 * window (not a tab). The `popup` keyword plus explicit dimensions is what makes
 * browsers spawn a standalone window; it is sized to a portion of the available
 * screen and centred, falling back to sensible defaults when screen metrics are
 * unavailable (e.g. in tests).
 * @returns {string} The comma-separated window features.
 */
function viewerWindowFeatures(): string {
  const screen = globalThis.screen;
  const width = Math.round((screen?.availWidth ?? 1280) * 0.7);
  const height = Math.round((screen?.availHeight ?? 900) * 0.85);
  const left = Math.round(((screen?.availWidth ?? 1280) - width) / 2);
  const top = Math.round(((screen?.availHeight ?? 900) - height) / 2);
  return `popup,width=${width},height=${height},left=${left},top=${top}`;
}

/**
 * Opens (or refocuses) the single document viewer window (OR-22558). The URL
 * includes the runtime basename because `window.open` — unlike React Router
 * links — needs the full path. A features string is passed so the viewer opens
 * as a separate browser window rather than a tab.
 * @returns {void}
 */
export function openDocumentViewerWindow(): void {
  if (viewerWindow && !viewerWindow.closed) {
    viewerWindow.focus();
    return;
  }
  // If viewer already open in another tab, don't open new window.
  // The broadcaster in that tab will handle updates via BroadcastChannel.
  const lockData = localStorage.getItem(OPENER_LOCK_KEY);
  if (lockData) {
    try {
      const { timestamp } = JSON.parse(lockData);
      const AGE_MS = Date.now() - timestamp;
      const STALE_THRESHOLD = 5 * 60 * 1000; // 5 minutes

      // If lock is fresh, don't open (another tab is handling it).
      // If stale, treat as crashed opener and allow new open.
      if (AGE_MS < STALE_THRESHOLD) {
        return;
      }
    } catch {
      // Malformed lock data, clear it and proceed
      localStorage.removeItem(OPENER_LOCK_KEY);
    }
  }
  // The lock itself is written by the viewer window on mount (DocumentViewer),
  // using its own tabId — writing it here would store the opener's tabId, which
  // the viewer's beforeunload cleanup could never match against itself.
  const url = `${getBasename()}${DOCUMENT_VIEWER_PATH}`;
  viewerWindow = globalThis.open(url, VIEWER_WINDOW_NAME, viewerWindowFeatures()) ?? undefined;
  viewerWindow?.focus();
}
